import { rest } from '@0xtorch/core'
import { z } from 'zod'
import type { ClientCallParameters } from './create'

const signatureSchema = z.object({
  /** estimated production time, as Unix timestamp (seconds since the Unix epoch) of when transaction was processed. null if not available. */
  blockTime: z.number().int().nullable(),
  confirmationStatus: z
    .union([
      z.literal('processed'),
      z.literal('confirmed'),
      z.literal('finalized'),
    ])
    .nullable(),
  /** rror if transaction failed, null if transaction succeeded. See [TransactionError definitions](https://github.com/solana-labs/solana/blob/c0c60386544ec9a9ec7119229f37386d9f070523/sdk/src/transaction/error.rs#L13) for more info. */
  err: z.unknown().nullable(),
  /** Memo associated with the transaction, null if no memo is present */
  memo: z.string().nullable(),
  /** transaction signature as base-58 encoded string */
  signature: z.string(),
  /** The slot that contains the block with the transaction */
  slot: z.number().int(),
})

export type Signature = z.infer<typeof signatureSchema>

const schema = z.object({
  jsonrpc: z.literal('2.0'),
  result: z.array(signatureSchema),
  id: z.number(),
})

type GetSignaturesForAddressParameters = ClientCallParameters & {
  readonly address: string
  readonly before?: string
  readonly until?: string
}

export const getSignaturesForAddress = async ({
  endpoint,
  headers,
  address,
  before,
  until,
}: GetSignaturesForAddressParameters): Promise<readonly Signature[]> => {
  const response = await rest(endpoint, {
    fetchOptions: {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'getSignaturesForAddress',
        params: [
          address,
          ...(before === undefined && until === undefined
            ? []
            : [
                {
                  before,
                  until,
                },
              ]),
        ],
      }),
    },
    schema: schema,
  })
  return response.result
}
